import type { EnvelopeRecipient } from '@/components/accounting/reports/envelope-sheet';

/**
 * Hand-off contract between the bulk-mailings selector
 * (`app/associations/[id]/statements.tsx`) and the bulk print page
 * (`app/print/statements/index.tsx`).
 *
 * The print page opens in a NEW TAB via `window.open`. A run can cover 100+
 * accounts, so passing the selection through the URL would blow past the
 * browser's URL-length limit. Instead the selector writes the resolved job to
 * `localStorage` under a one-shot token and opens `…?job=<token>`; the print
 * page reads it once and deletes it. `localStorage` (not `sessionStorage`) so
 * the value is reliably visible in the freshly-opened tab.
 *
 * Statements carry only account ids + the statement (end) date — each
 * statement's start auto-computes per account server-side. Envelopes carry
 * fully-resolved recipients + the return block, so the print page needs no
 * extra fetches.
 */

export interface StatementsJob {
	mode: 'statements';
	associationId: string;
	/** Statement/end date (epoch ms). Per-account start is auto-computed. */
	endDate?: number;
	accountIds: string[];
}

export interface EnvelopesJob {
	mode: 'envelopes';
	associationId: string;
	/** Org return-address block, pre-split into lines. */
	returnLines: string[];
	recipients: EnvelopeRecipient[];
	/** Optional red attention notice (e.g. "ANNUAL MEETING NOTICE"). */
	redNotice?: string;
	/** Optional org logo (base64 data URL) printed top-left above the return address. */
	logo?: string;
	/** Return-address text color (hex). Defaults to dark blue when unset. */
	brandColor?: string;
	/** Print the logo + return address. OFF by default (pre-printed envelopes). */
	showReturnAddress?: boolean;
}

export type BulkMailingsJob = StatementsJob | EnvelopesJob;

const keyFor = (token: string): string => `bulk-mailings:${token}`;

/**
 * Persist a job and return its URL token. Web-only (printing is web-only).
 * Throws if localStorage is unavailable so the caller can surface an error
 * rather than silently opening an empty print tab.
 */
export const writeMailingsJob = (token: string, job: BulkMailingsJob): void => {
	if (typeof localStorage === 'undefined') throw new Error('Bulk printing is only available on web.');
	localStorage.setItem(keyFor(token), JSON.stringify(job));
};

/** Read a job by token and delete it (one-shot consume). Returns null when missing/corrupt. */
export const consumeMailingsJob = (token: string): BulkMailingsJob | null => {
	if (typeof localStorage === 'undefined') return null;
	const raw = localStorage.getItem(keyFor(token));
	if (raw == null) return null;
	localStorage.removeItem(keyFor(token));
	try {
		return JSON.parse(raw) as BulkMailingsJob;
	} catch {
		return null;
	}
};
